import {
  type SwappedSellOrderData,
  type SwappedWithdrawalLegs,
  ensureRelayClientInitialized,
  getPublicClientFor,
  isEvmChainId,
  isUserRejectedError,
  processFiatWithdrawalOrder,
  toFiatWithdrawalOrder,
} from '@funkit/connect-core'
import { useCallback, useRef } from 'react'

import type {
  WithdrawalFlowCallbacks,
  WithdrawalFlowConfig,
} from '../flow/withdrawalFlowConfig'
import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import { resolveFiatTargetDecimals } from './fiatTargetDecimals'
import { WALLETLESS_WITHDRAWAL_ERROR } from './walletlessWithdrawal'

// Module-scoped so a submitted order stays deduped across sheet close/reopen —
// the hook (and its refs) unmount on close, but the embed can re-emit
// SWAPPED_ORDER_DATA for the same order_id after a reopen. Provider order ids
// are globally unique, so a process-wide set is safe.
const submittedOrderIds = new Set<string>()

/**
 * @internal Test-only: clear the process-wide submitted-order dedupe set so
 * suites that reuse order ids stay isolated. Not part of the public API.
 */
export function __resetSubmittedWithdrawalOrders(): void {
  submittedOrderIds.clear()
}

export interface UseWithdrawalExecutionParams {
  config: WithdrawalFlowConfig
  /** The route resolved by `useWithdrawalFops` — one resolution for fetch + quote. */
  route: SwappedWithdrawalLegs
  callbacks: WithdrawalFlowCallbacks
  /** Unrecoverable failure — the flow shows its error state. */
  onError: () => void
}

/**
 * Thin React wrapper over connect-core's `processFiatWithdrawalOrder`: owns
 * the control-flow guards for the unreliable embed message stream and maps
 * outcomes onto host callbacks. The guards are intentionally refs, not state —
 * both are read synchronously inside the async `executeOrder` (a re-entrancy
 * flag set before the first await, and a dedupe set) and must not re-render.
 */
export function useWithdrawalExecution({
  config,
  route,
  callbacks,
  onError,
}: UseWithdrawalExecutionParams): {
  executeOrder: (data: SwappedSellOrderData) => Promise<void>
} {
  const { apiKey, userId } = useFunkitIdentity()

  const isProcessingRef = useRef(false)

  const executeOrder = useCallback(
    async (data: SwappedSellOrderData) => {
      const order = toFiatWithdrawalOrder(data)
      if (isProcessingRef.current) {
        logger.warn('connect-rn:withdrawal:already_processing', {
          order_id: order.orderId,
        })
        return
      }
      if (submittedOrderIds.has(order.orderId)) {
        logger.warn('connect-rn:withdrawal:duplicate', {
          order_id: order.orderId,
        })
        return
      }
      isProcessingRef.current = true

      // A non-EVM target has no ERC-20 to read, so its decimals come from the
      // config token the route resolved to.
      const targetDecimals = resolveFiatTargetDecimals({ config, route })

      // Routing decision point: the requested target (config) vs the enforced
      // target (after the Statsig source override) alongside what the provider
      // reported it accepts, for off-ramp reconciliation (web parity).
      logger.info('connect-rn:withdrawal:route_decision', {
        order_id: order.orderId,
        overrideApplied: route.overrideApplied,
        targetDecimalsSource: targetDecimals == null ? 'chain' : 'config',
        requestedTargetChainId: config.swappedAcceptedToken?.chainId,
        requestedTargetTokenAddress: config.swappedAcceptedToken?.address,
        enforcedTargetChainId: route.targetChainId,
        enforcedTargetTokenAddress: route.targetTokenAddress,
        enforcedTargetTokenSymbol: route.targetTokenSymbol,
        providerCrypto: order.providerCrypto,
        providerNetwork: order.providerNetwork,
      })

      let txHash: string
      try {
        // Backstop: `useWithdrawalFops` already withholds cash methods without
        // a wallet, so reaching here means the embed emitted an order we have
        // no signer for. Fail before relay init rather than inside the rail.
        const { withdrawalWallet } = config
        if (!withdrawalWallet) {
          // Its own event, not just the `execution_failed` the catch logs: the
          // fops gate should have withheld cash entirely, so reaching here
          // means that gate leaked and is worth alerting on by itself.
          logger.error(
            'connect-rn:withdrawal:walletless_config',
            new Error(WALLETLESS_WITHDRAWAL_ERROR),
            { order_id: order.orderId },
          )
          throw new Error(WALLETLESS_WITHDRAWAL_ERROR)
        }
        ensureRelayClientInitialized(logger, 'funkit-connect-rn')
        ;({ txHash } = await processFiatWithdrawalOrder({
          order,
          wallet: withdrawalWallet,
          legs: route,
          apiKey,
          userId,
          logger,
          // An EVM target reads its decimals on-chain rather than trusting the
          // host config — a stale value would mis-scale the transfer. Off EVM
          // there is nothing to read, so `targetDecimals` carries them.
          publicClient: isEvmChainId(route.targetChainId)
            ? getPublicClientFor(Number(route.targetChainId))
            : undefined,
          targetDecimals,
        }))
      } catch (error) {
        if (isUserRejectedError(error)) {
          // The user declined the signature — the embed stays interactive.
          logger.warn('connect-rn:withdrawal:user_rejected', {
            order_id: order.orderId,
          })
          return
        }
        // Unrecoverable: funds did not move (or the record failed) and the
        // user sees the error state.
        logger.error('connect-rn:withdrawal:execution_failed', error as Error, {
          order_id: order.orderId,
        })
        callbacks.onWithdrawalError?.({
          message: error instanceof Error ? error.message : String(error),
        })
        onError()
        return
      } finally {
        isProcessingRef.current = false
      }

      // Funds moved — mark submitted and correlate the provider order id to the
      // on-chain hash. The host callback runs in its own guard: a throw here is
      // NOT an execution failure (money already moved), so it must not fall into
      // the error path above and fire `onWithdrawalError` for a success.
      submittedOrderIds.add(order.orderId)
      try {
        callbacks.onWithdrawalSubmitted?.(order.orderId, txHash)
      } catch (error) {
        logger.warn('connect-rn:withdrawal:submitted_callback_threw', {
          order_id: order.orderId,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    },
    [apiKey, userId, config, route, callbacks, onError],
  )

  return { executeOrder }
}
